import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, from, switchMap } from 'rxjs';
import { PaginatedItems } from '../../models/types';
import { Provider } from '../../models/provider.model';
import { BaseService } from '../../services/_base.service';
import { UserService } from '../../services/user.service';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { CameraService } from '../../shared/image-uploader/camera/camera.service';
import { FirebaseService } from '../../services/firebase.service';

@Injectable({
  providedIn: 'root',
})
export class ProvidersService extends BaseService {
  params = [
    'address',
    'bank_account_number',
    'bank_account_type',
    'bank_name',
    'email',
    'name',
    'payment_method',
    'phone',
    'picture',
    'rut',
  ];

  model = 'Provider';

  providers: BehaviorSubject<Provider[]> = new BehaviorSubject([]);

  providers$ = this.providers.asObservable();

  private readonly cameraService = inject(CameraService);

  private readonly firebaseService = inject(FirebaseService);

  constructor(
    public http: HttpClient,
    userService: UserService,
  ) {
    super()
    this.all().subscribe((providers) => this.providers.next(providers.body));
  }

  index(value = null, page = 1) {
    return this.http.get<PaginatedItems<Provider>>(
      `${this.adminURL}/providers`,
      this.getParams({ search: value, page }),
    );
  }

  all() {
    return this.http.get<Provider[]>(`${this.adminURL}/providers/all`, this.getParams());
  }

  create(value: Partial<Provider>) {
    if (value.picture) {
      return from(this.uploadImage(value.picture as any)).pipe(
        switchMap((imageUrl) => {
          value.picture = imageUrl;
          return this.http.post<Provider>(`${this.adminURL}/providers/`, {
            provider: this.permitParams(value, ...this.params),
          }, this.getParams());
        }),
      );
    } else {
      return this.http.post<Provider>(`${this.adminURL}/providers/`, {
        provider: this.permitParams(value, ...this.params),
      }, this.getParams());
    }
  }

  update(id: number, value: Provider) {
    if (typeof value.picture === 'object') {
      return from(this.uploadImage(value.picture as any)).pipe(
        switchMap((imageUrl) => {
          value.picture = imageUrl;
          return this.http.put<Provider>(
            `${this.adminURL}/providers/${id}`,
            { provider: this.permitParams(value, ...this.params) },
            this.getParams(),
          );
        }),
      );
    } else {
      return this.http.put<Provider>(
        `${this.adminURL}/providers/${id}`,
        { provider: this.permitParams(value, ...this.params) },
        this.getParams(),
      );
    }

  }

  destroy(id: number) {
    return this.http.delete(`${this.adminURL}/providers/${id}`, this.getParams());
  }

  restore(id: number) {
    return this.http.delete(`${this.adminURL}/providers/${id}/restore`, this.getParams());
  }

  swap(ids: number[]) {
    return this.http.post(`${this.adminURL}/providers/swap`, { ids }, this.getParams());
  }

  private uploadImage(file: File): Promise<string> {
    const storage = this.firebaseService.storage;
    const filePath = this.cameraService.generateFilePath('png');
    const fileRef = ref(storage, filePath);

    return uploadBytes(fileRef, file, {
      cacheControl: 'public,max-age=15552000',
    }).then(snapshot => getDownloadURL(snapshot.ref));
  }
}
